import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { securityApi, type ChallengeOption } from '../api/endpoints.ts';
import { requestAssertion, wasDismissed, type ServerPublicKeyRequest } from '../lib/webauthn.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, LoadingBlock, TextInput } from './ui.tsx';

/**
 * "Confirm it's you" — the dialog behind the freshness gate.
 *
 * The server answers a sensitive action it will not perform with a 403 and a
 * distinguishable code (`FRESH_AUTH_REQUIRED`), which exists precisely so the
 * browser can offer a way THROUGH rather than an apology. Without this, the
 * gate reads as "you do not have permission" for something the person plainly
 * does — and the only way past it is to sign out and back in, which is both
 * mysterious and, on a remembered device, ineffective.
 *
 * `MFA_SETUP_REQUIRED` is the other half of the same 403 and is deliberately
 * NOT handled here: it means nothing is enrolled, and the answer is the
 * enrollment screen, not a code prompt.
 */
export function isFreshAuthRequired(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'FRESH_AUTH_REQUIRED';
}

export function isMfaSetupRequired(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'MFA_SETUP_REQUIRED';
}

export default function ElevateDialog({ onVerified, onCancel }: { onVerified: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);

  const options = useQuery({ queryKey: ['elevate-options'], queryFn: () => securityApi.elevateOptions() });
  const list: ChallengeOption[] = options.data?.options ?? [];
  const [selected, setSelected] = useState<ChallengeOption | null>(null);
  const choice = selected ?? list[0] ?? null;

  // An authenticator app needs no round trip to produce a code, so asking for
  // one would be a wasted request and a wasted second. A text message does.
  useEffect(() => {
    // TOTP needs no round trip to produce a code; everything else — a text
    // message to send, a security-key request to sign — does.
    if (!choice || challengeId || choice.type === 'totp' || choice.type === 'recovery') return;
    startChallenge.mutate(choice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice?.methodId, choice?.type]);

  const startChallenge = useMutation({
    mutationFn: (option: ChallengeOption) =>
      securityApi.elevateChallenge({ type: option.type, methodId: option.methodId }),
    onSuccess: async (result) => {
      setChallengeId(result.challengeId);
      setError(null);
      // A security key has no code to type: the prompt IS the answer, so it
      // runs here rather than waiting for a submit with nothing to submit.
      // Without this the dialog offers a passkey and then cannot accept one,
      // which locks a passkey-only account out of every protected action.
      if (result.publicKey) await finishWithKey(result.challengeId, result.publicKey);
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('security.elevateError')),
  });

  async function finishWithKey(id: string, publicKey: ServerPublicKeyRequest) {
    try {
      const assertion = await requestAssertion(publicKey);
      // Dismissing the browser prompt is a choice, not an error.
      if (!assertion) return;
      await securityApi.elevateVerify({ challengeId: id, assertion });
      setCode('');
      setChallengeId(null);
      onVerified();
    } catch (caught) {
      if (wasDismissed(caught)) return;
      setChallengeId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('security.elevateError'));
    }
  }

  const verify = useMutation({
    mutationFn: () => {
      // TOTP verifies against a challenge too, so one is created on submit for
      // the case where none was needed up front.
      const ensure = challengeId
        ? Promise.resolve({ challengeId })
        : securityApi.elevateChallenge({ type: choice?.type ?? 'totp', methodId: choice?.methodId ?? null });
      return ensure.then((challenge) => securityApi.elevateVerify({ challengeId: challenge.challengeId, code }));
    },
    onSuccess: () => {
      setCode('');
      setChallengeId(null);
      onVerified();
    },
    onError: (caught) => {
      // A spent or exhausted challenge cannot be answered again; drop it so the
      // next attempt asks for a fresh one rather than failing identically.
      setChallengeId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('security.elevateError'));
    },
  });

  if (options.isLoading)
    return (
      <div className="rounded border border-slate-300 bg-white p-4 shadow-sm" role="dialog" aria-modal="true">
        <h3 className="font-semibold">{t('security.elevateTitle')}</h3>
        <LoadingBlock />
        <Button variant="secondary" type="button" className="mt-3" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    );

  return (
    <div className="rounded border border-slate-300 bg-white p-4 shadow-sm" role="dialog" aria-modal="true">
      <h3 className="font-semibold">{t('security.elevateTitle')}</h3>
      <p className="mt-1 text-sm text-slate-600">{t('security.elevateIntro')}</p>

      {options.isError ? (
        <div className="mt-3">
          <Alert tone="error">{t('security.elevateError')}</Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {list.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {list.map((option) => (
            <Button
              key={`${option.type}:${option.methodId ?? 'none'}`}
              variant={choice === option ? 'primary' : 'secondary'}
              className="max-w-full"
              style={{ overflowWrap: 'anywhere' }}
              onClick={() => {
                setSelected(option);
                setChallengeId(null);
                setCode('');
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      ) : null}

      <form
        className="mt-3 space-y-3"
        onSubmit={(submitted) => {
          submitted.preventDefault();
          verify.mutate();
        }}
      >
        {choice?.type === 'webauthn' ? (
          // A security key answers through the platform prompt, so there is no
          // code to type — and a dismissed prompt has to leave a way back to
          // it. Rendering the code form here stranded a passkey-only user with
          // a field they could not fill and a button that could not succeed.
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => choice && startChallenge.mutate(choice)}
              disabled={startChallenge.isPending}
            >
              {t('auth.mfa.useSecurityKey')}
            </Button>
            <Button variant="secondary" type="button" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        ) : (
          <>
            <TextInput
              id="security-elevate-code"
              label={t('security.elevateCodeLabel')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              // A recovery code is Crockford base32 — `ABCD-EFGH-IJKL` — so a
              // numeric keyboard hides the letters the credential is made of.
              autoComplete={choice?.type === 'recovery' ? 'off' : 'one-time-code'}
              inputMode={choice?.type === 'recovery' ? 'text' : 'numeric'}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={verify.isPending || code.length === 0}>
                {t('security.elevateConfirm')}
              </Button>
              <Button variant="secondary" type="button" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
